import { isExternalLink } from "../../helpers/isExternalLink";
import { ButtonPcs } from "../ButtonPcs/ButtonPcs";
import {
  CosmosButtonProps,
  CosmosIconAmazon,
  CosmosIconChevronDown,
  CosmosIconExternalLink,
  CosmosIconGlobeGeo,
  CosmosIconShop,
  CosmosText,
} from "@cosmos/web-react";
import classNames from "classnames";
import classNamesBind from "classnames/bind";
import { ComponentProps, useState } from "react";
import styles from "./SelectButton.module.css";

const cx = classNamesBind.bind(styles);

interface items {
  url: string;
  icon?: string; // "amazon" | "shop"
  label: string;
  onClick?: () => void;
}

export const SelectButton = ({
  buttonClassName,
  disabled,
  buttonKind = "primary",
  buttonSize = "medium",
  buttonAppearance = "dark",
  label,
  items,
  id,
  buttonCustomColor,
  listboxPosition = "bottom",
}: {
  buttonClassName?: string;
  disabled?: boolean;
  buttonKind?: CosmosButtonProps["kind"];
  buttonSize: CosmosButtonProps["size"];
  buttonAppearance?: CosmosButtonProps["appearance"];
  label: string; // Label is only used when there are more than 1 vendor links
  items: items[];
  id: string; // ID is only used when there are more than 1 vendor links
  buttonCustomColor?: ComponentProps<typeof ButtonPcs>["customColor"];
  listboxPosition?: "top" | "bottom";
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const onClick = () => setIsExpanded((current) => !current);

  return (
    <>
      {items.length === 1 ? (
        <>
          {items.map((item, index) => (
            <ButtonPcs
              key={index}
              className={classNames(buttonClassName)}
              href={item.url}
              target={isExternalLink(item.url ?? "") ? "_blank" : undefined}
              icon-placement="before"
              kind={buttonKind}
              onClick={item.onClick}
              size={buttonSize}
              appearance={buttonAppearance}
              customColor={buttonCustomColor}
            >
              {item.icon === "amazon" ? (
                <CosmosIconAmazon slot="icon" aria-hidden="true" />
              ) : item.icon === "shop" ? (
                <CosmosIconShop slot="icon" aria-hidden="true" />
              ) : (
                <CosmosIconGlobeGeo slot="icon" aria-hidden="true" />
              )}
              {item.label}
            </ButtonPcs>
          ))}
        </>
      ) : (
        <div className={cx("container")}>
          <ButtonPcs
            className={classNames(buttonClassName, cx("button"))}
            shape="pill"
            size={buttonSize}
            appearance={buttonAppearance}
            icon-placement="after"
            onClick={onClick}
            disabled={disabled}
            kind={buttonKind}
            type="button" // prevent accidental form submits
            aria-haspopup="listbox"
            aria-expanded={isExpanded ? true : undefined}
            aria-controls={`${id}-button-select`}
            onBlur={(event) => {
              if (event.relatedTarget === null && isExpanded) {
                setIsExpanded(false);
              }
            }}
            customColor={buttonCustomColor}
          >
            <CosmosIconChevronDown
              aria-hidden="true"
              className={cx("icon")}
              slot="icon"
            />
            {label}
          </ButtonPcs>
          <div
            hidden={!isExpanded}
            id={`${id}-button-select`}
            role="listbox"
            className={cx("listbox")}
            data-open={isExpanded}
            data-position={listboxPosition}
          >
            {items.map((item, index) => (
              <a
                key={index}
                className={cx("listbox__option")}
                href={item.url}
                target={isExternalLink(item.url ?? "") ? "_blank" : undefined}
                onClick={item.onClick}
              >
                <div className={cx("listbox__option-content")}>
                  {item.icon === "amazon" ? (
                    <CosmosIconAmazon
                      aria-hidden="true"
                      className={cx("listbox__option-prefix")}
                      slot="icon"
                    />
                  ) : item.icon === "shop" ? (
                    <CosmosIconShop
                      aria-hidden="true"
                      className={cx("listbox__option-prefix")}
                      slot="icon"
                    />
                  ) : (
                    <CosmosIconGlobeGeo
                      aria-hidden="true"
                      className={cx("listbox__option-prefix")}
                      slot="icon"
                    />
                  )}

                  <CosmosText
                    className={cx("listbox__option-label")}
                    size="small"
                    clamp={1}
                  >
                    {item.label}
                  </CosmosText>

                  <CosmosIconExternalLink
                    className={cx("listbox__option-suffix")}
                  />
                </div>
              </a>
            ))}
          </div>
        </div>
      )}
    </>
  );
};
